/**
 * Onderdeel Documenten Lijst Pagina
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiFetch, { getApiBaseUrl } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import ErrorDialog from '../../components/ErrorDialog';
import './Onderdelen.css';

interface OnderdeelDocument {
  id: number;
  voorraad_item_id: number;
  document_type: string;
  titel: string | null;
  bestand_pad: string;
  taal: string | null;
  versie: string | null;
  omschrijving: string | null;
  actief: boolean;
  created_at: string;
  updated_at: string | null;
}

interface VoorraadItem {
  id: number;
  onderdeel_type: string;
  fabrikant: string | null;
  model: string | null;
  leverancier: string | null;
  artikelnummer: string | null;
}

const OnderdeelDocumentenList = () => {
  const navigate = useNavigate();
  const { onderdeelId } = useParams<{ onderdeelId: string }>();
  const [documenten, setDocumenten] = useState<OnderdeelDocument[]>([]);
  const [onderdeel, setOnderdeel] = useState<VoorraadItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: number; naam: string } | null>(null);

  const loadData = useCallback(async () => {
    if (!onderdeelId) return;

    try {
      setLoading(true);
      setError(null);

      // Haal voorraad item en documenten op
      const [onderdeelData, documentenData] = await Promise.all([
        apiFetch(`/api/v1/voorraad/items/${onderdeelId}`),
        apiFetch(`/api/v1/onderdeel-documenten?voorraad_item_id=${onderdeelId}`)
      ]);

      setOnderdeel(onderdeelData);
      setDocumenten(documentenData);
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden bij het ophalen van de gegevens');
    } finally {
      setLoading(false);
    }
  }, [onderdeelId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeleteClick = (id: number, naam: string) => {
    setConfirmDelete({ id, naam });
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;

    try {
      await apiFetch(`/api/v1/onderdeel-documenten/${confirmDelete.id}`, {
        method: 'DELETE'
      });
      setConfirmDelete(null);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden bij het verwijderen');
      setConfirmDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setConfirmDelete(null);
  };

  const handleOpenDocument = async (document: OnderdeelDocument) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${getApiBaseUrl()}/api/v1/onderdeel-documenten/${document.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        setError(`Fout bij ophalen document: ${error.detail || 'Onbekende fout'}`);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (err: any) {
      setError(`Fout bij ophalen document: ${err.message || 'Onbekende fout'}`);
    }
  };

  if (loading) {
    return <div className="loading">Laden...</div>;
  }

  if (!onderdeel) {
    return <div className="error-message">Onderdeel niet gevonden</div>;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Documenten - {onderdeel.onderdeel_type} {onderdeel.fabrikant ? `(${onderdeel.fabrikant})` : ''}</h1>
        <button
          className="btn-primary"
          onClick={() => navigate(`/onderdelen/${onderdeelId}/documenten/new`)}
        >
          + Nieuw Document
        </button>
      </div>

      {/* Vaste header rij - Desktop */}
      {documenten.length > 0 && (
        <div className="table-header" style={{
          display: 'flex',
          backgroundColor: '#f8f9fa',
          borderBottom: '2px solid #dee2e6',
          padding: '0.75rem 1rem',
          marginTop: '1rem',
          borderRadius: '8px 8px 0 0',
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#495057'
        }}>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Type</div>
          <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem' }}>Titel</div>
          <div style={{ flex: '0.5', minWidth: 0, paddingRight: '1rem' }}>Taal</div>
          <div style={{ flex: '0.5', minWidth: 0, paddingRight: '1rem' }}>Versie</div>
          <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem' }}>Bestand</div>
          <div style={{ flex: '1.5', minWidth: 0 }}>Acties</div>
        </div>
      )}

      {/* Items lijst */}
      {documenten.length === 0 ? (
        <div style={{
          padding: '3rem',
          backgroundColor: '#f1f5f9',
          borderRadius: '8px',
          textAlign: 'center',
          marginTop: '1rem'
        }}>
          <p style={{ color: '#64748b' }}>Nog geen documenten toegevoegd</p>
        </div>
      ) : (
        <>
          {/* Desktop tabel */}
          <div className="desktop-table" style={{
            background: 'white',
            borderRadius: '0 0 8px 8px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            {documenten.map((document) => (
              <div key={document.id} style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0.75rem 1rem',
                borderBottom: '1px solid #dee2e6',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{document.document_type}</div>
                <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{document.titel || '-'}</div>
                <div style={{ flex: '0.5', minWidth: 0, paddingRight: '1rem' }}>{document.taal || '-'}</div>
                <div style={{ flex: '0.5', minWidth: 0, paddingRight: '1rem' }}>{document.versie || '-'}</div>
                <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem', fontSize: '0.85rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {document.bestand_pad.split('/').pop()}
                </div>
                <div className="actions" style={{ flex: '1.5', minWidth: 0 }}>
                  <button
                    className="btn-small btn-success"
                    onClick={() => handleOpenDocument(document)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Open
                  </button>
                  <button
                    className="btn-small btn-secondary"
                    onClick={() => navigate(`/onderdelen/${onderdeelId}/documenten/${document.id}`)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Bewerken
                  </button>
                  <button
                    className="btn-small btn-danger"
                    onClick={() => handleDeleteClick(document.id, document.titel || document.document_type)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Verwijderen
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile tegels */}
          <div className="mobile-cards">
            {documenten.map((document) => (
              <div key={document.id} className="document-card" onClick={() => handleOpenDocument(document)}>
                <div className="document-card-header">
                  <span className="document-type">{document.document_type}</span>
                  {document.taal && <span className="document-taal">{document.taal}</span>}
                </div>
                <div className="document-card-body">
                  {document.titel && <div className="document-info"><span className="label">Titel:</span> {document.titel}</div>}
                  <div className="document-info"><span className="label">Bestand:</span> {document.bestand_pad.split('/').pop()}</div>
                  {document.versie && <div className="document-info"><span className="label">Versie:</span> {document.versie}</div>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Document Verwijderen"
        message={`Weet je zeker dat je document "${confirmDelete?.naam}" wilt verwijderen?`}
        confirmText="Verwijderen"
        cancelText="Annuleren"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        danger={true}
      />

      <ErrorDialog
        isOpen={!!error}
        message={error || ''}
        onClose={() => setError(null)}
      />
    </div>
  );
};

export default OnderdeelDocumentenList;